import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, HostListener, signal, viewChild, ElementRef, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { injectVirtualizer } from '@tanstack/angular-virtual';
import { HighlightTextPipe } from 'src/app/pipe/highlight-text.pipe';

@Component({
  selector: 'app-search-results',
  standalone: true,
  imports: [CommonModule, InfiniteScrollModule, HighlightTextPipe],
  templateUrl: './search-results.component.html',
  styleUrls: ['./search-results.component.css']
})
export class SearchResultsComponent implements OnChanges, AfterViewInit {
  @Input() results: any[] = [];
  @Input() matchesCount: number = 0;
  @Input() filters: any = {};
  @Input() isLoading: boolean = false;
  @Output() selectChat = new EventEmitter<any>();
  @Output() closeResults = new EventEmitter<void>();
  @Output() scrolled = new EventEmitter<void>();
  @Output() openPreview = new EventEmitter<{ chat: any, allMedia: any[] }>();

  // Use viewChild from @angular/core
  resultsScrollElement = viewChild<ElementRef<HTMLDivElement>>("resultsScrollElement");

  // Virtualizer setup
  virtualGridData = signal<any[]>([]);
  virtualizer = injectVirtualizer(() => ({
    scrollElement: this.resultsScrollElement()?.nativeElement,
    count: this.virtualGridData().length,
    estimateSize: () => this.getRowHeight(),
    overscan: 5,
  }));

  constructor(private cdr: ChangeDetectorRef) { }

  @HostListener("window:resize", ["$event"])
  onResize() {
    this.updateVirtualGridData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['results'] || changes['filters']) {
      this.updateVirtualGridData();
    }
  }

  ngAfterViewInit() {
    // Re-calculate based on actual container width now that it's rendered
    setTimeout(() => {
      this.updateVirtualGridData();
    });
  }

  private getItemsPerRow(): number {
    if (this.filters.selectedType !== 'photo' && this.filters.selectedType !== 'video') {
      return 1;
    }

    // We get the actual container width instead of window.innerWidth
    // Provide a reasonable fallback for first render
    const el = this.resultsScrollElement()?.nativeElement;
    const containerWidth = el ? el.clientWidth : (window.innerWidth - 300); // 300px approx sidebar width

    // Matches CSS: grid-template-columns: repeat(auto-fill, minmax(115px, 1fr)) with 0.8rem (13px) gap
    const minItemWidth = 110;
    const gap = 2;

    // Formula: (n * minItemWidth) + ((n - 1) * gap) <= containerWidth
    let cols = Math.floor((containerWidth + gap) / (minItemWidth + gap));
    return Math.max(2, cols); // At least 2 columns usually
  }

  private getRowHeight(): number {
    if (this.filters.selectedType === 'photo' || this.filters.selectedType === 'video') {
      const el = this.resultsScrollElement()?.nativeElement;
      if (!el) return 132;

      const containerWidth = el.clientWidth;
      const itemsPerRow = this.getItemsPerRow();
      const gap = 2; // matches CSS gap

      // Calculate width of one item based on container width and gaps
      // Formula: containerWidth = (n * itemWidth) + ((n - 1) * gap)
      const itemWidth = (containerWidth - (itemsPerRow - 1) * gap) / itemsPerRow;

      // Since aspect-ratio is 1/1, height equals itemWidth + gap
      return itemWidth + gap;
    }
    return 52;
  }

  private updateVirtualGridData() {
    const newVirtualData: any[] = [];
    const itemsPerRow = this.getItemsPerRow();

    for (let i = 0; i < this.results.length; i += itemsPerRow) {
      const rowItems = this.results.slice(i, i + itemsPerRow);
      newVirtualData.push({
        type: this.filters.selectedType || 'default',
        items: rowItems,
        itemsPerRow: itemsPerRow
      });
    }

    this.virtualGridData.set(newVirtualData);
    this.cdr.detectChanges();
  }

  onScroll() {
    this.scrolled.emit();
  }

  onChatClick(chat: any) {
    if (this.canPreviewMedia()) {
      this.openPreview.emit({ chat, allMedia: this.results });
    } else {
      this.selectChat.emit({ ...chat, fromSearch: true, isAdvancedSearch: true, lastMessage: chat });
    }
  }

  canPreviewMedia(): boolean {
    return this.filters.selectedType === 'photo' || this.filters.selectedType === 'video';
  }

  onClose() {
    this.closeResults.emit();
  }

  /** Extract file extension from message/fileUrl */
  getDocExtension(chat: any): string {
    const src = chat.meta?.fileUrl || chat.fileUrl || chat.message || '';
    const match = src.match(/\.([a-zA-Z0-9]+)(\?|$)/);
    return match ? match[1].toLowerCase() : 'file';
  }

  /** Get a short uppercase label like PDF, DOC, XLS, PPT */
  getDocLabel(chat: any): string {
    const ext = this.getDocExtension(chat);
    const map: Record<string, string> = {
      pdf: 'PDF', doc: 'DOC', docx: 'DOC',
      xls: 'XLS', xlsx: 'XLS', csv: 'CSV',
      ppt: 'PPT', pptx: 'PPT', txt: 'TXT'
    };
    return map[ext] || ext.toUpperCase();
  }

  /** Get icon background color for each doc type */
  getDocColor(chat: any): string {
    const ext = this.getDocExtension(chat);
    const map: Record<string, string> = {
      pdf: '#E53E3E',
      doc: '#2B6CB0', docx: '#2B6CB0',
      xls: '#276749', xlsx: '#276749', csv: '#276749',
      ppt: '#C05621', pptx: '#C05621',
      txt: '#718096'
    };
    return map[ext] || '#6565ac';
  }


  getCleanFileName(chat: any): string {
    if (chat.meta?.ogFileName) {
      return chat.meta.ogFileName;
    }
    const src = chat.meta?.fileUrl || chat.fileUrl || chat.message || '';

    // If it's a link, don't treat it as a file to clean
    if (this.isLink(chat)) {
      return chat.message || '';
    }

    // Get just the last segment after final /
    const base = src.split('/').pop() || src;
    // Remove ALL leading numeric-timestamp + dash-or-underscore groups
    const cleaned = base.replace(/^(\d{10,}[-_])+/, '');
    return decodeURIComponent(cleaned);
  }

  isLink(chat: any): boolean {
    const type = chat.typeTag || chat.contentType || chat.meta?.contentType || '';
    const message = chat.message || chat.lastMessage?.message || '';
    return type === 'link' || /^(http|https):\/\/[^ "]+$/.test(message.trim());
  }

  getLinkDomain(url: string): string {
    if (!url) return '';
    try {
      const domain = new URL(url).hostname;
      return domain.replace('www.', '');
    } catch {
      return url;
    }
  }
}

